import { Banner, Button, LoadingSpinner, Stack, Text, TextStyle } from '@cheqroom/ui';
import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import tw from 'twin.macro';

import { StyledCard } from '../../App.styles';
import { AuthFlowHeader } from '../AuthFlowHeader';
import { StyledScrollableStack } from '../SelectableListStep/SelectableListStep.styles';
import { useClearSession, useLoadWorkspaces } from './api.hooks';
import { Workspace } from './Workspace';

interface SelectWorkspaceStepState {
	state?: {
		organisationId?: string;
		error?: string;
	};
}

const StepSectionDivider = tw.hr`bg-gray-200 border-0 h-px`;

export const SelectWorkspaceStep: FC = () => {
	const { t } = useTranslation();
	const { state: locationState } = useLocation() as SelectWorkspaceStepState;

	const { workspaces, email, isLoading, selectWorkspace } = useLoadWorkspaces(locationState?.organisationId);
	const clearSession = useClearSession();

	const handleChangeEmail = () => {
		clearSession.mutate();
	};

	if (isLoading) {
		return <LoadingSpinner size="large" />;
	}

	return (
		<StyledCard sectioned>
			<Stack tw="gap-6" vertical>
				<AuthFlowHeader
					heading={t('choose_workspace.header')}
					subheading={t('choose_workspace.subheading')}
					subheadingPreserveLineBreaks
				/>

				<StepSectionDivider />

				{locationState?.error && <Banner variation="error">{locationState.error}</Banner>}

				{workspaces.length <= 0 && (
					<Banner variation="warning">{t('choose_workspace.email_not_in_workspaces')}</Banner>
				)}

				{workspaces.length > 0 && (
					<>
						<Text tw="whitespace-pre-line" align="center">
							<Trans
								i18nKey="choose_workspace.workspaces_title"
								values={{ email }}
								components={[
									<TextStyle key="0" variation="strong">
										placeholder
									</TextStyle>,
								]}
							/>
						</Text>

						<StyledScrollableStack spacing="tight" amountOfItems={workspaces.length} vertical>
							{workspaces.map((workspace) => (
								<Workspace
									key={workspace.id}
									workspace={workspace}
									ariaLabel={t('choose_workspace.aria_label', { name: workspace.name })}
									onClick={(event) => {
										event.preventDefault();
										selectWorkspace(workspace.url);
									}}
									to={workspace.url}
								/>
							))}
						</StyledScrollableStack>
					</>
				)}

				<Stack align="center" spacing="extraTight" vertical>
					<Text tw="whitespace-pre-line" align="center">
						{t('choose_workspace.missing_workspace')}
					</Text>
					<Button size="medium" type="button" inverted onClick={handleChangeEmail}>
						{t('choose_workspace.try_different_email')}
					</Button>
				</Stack>
			</Stack>
		</StyledCard>
	);
};
